import { administrativeLevelForCode } from "@/lib/boundaries/boundaryCache";

type DemandLocation = {
  id?: string;
  code?: string | null;
  parentId?: string | null;
  name?: string | null;
};

/**
 * A demand's location response is a flat list of its selected zones **plus**
 * their ancestors (district/county). The actual zones of interest are the
 * leaves — locations that aren't the parent of another location in the set.
 */
export function selectDemandZones<T extends DemandLocation>(
  locations: T[] | undefined,
): T[] {
  const list = locations ?? [];
  const parentIds = new Set(
    list.map((l) => l.parentId).filter((id): id is string => !!id),
  );
  const leaves = list.filter((l) => l.id && !parentIds.has(l.id));
  // Fall back to the raw list if there's no parent info to derive leaves from.
  return leaves.length ? leaves : list;
}

/**
 * Boundary nodes (code + administrative level) for every zone a demand targets,
 * so the map can outline all of them.
 */
export function selectDemandBoundaryNodes(
  locations: DemandLocation[] | undefined,
): { locationCode: string; administrative_level: string }[] {
  return selectDemandZones(locations)
    .filter((l): l is DemandLocation & { code: string } => !!l.code)
    .map((l) => ({
      locationCode: l.code,
      administrative_level: administrativeLevelForCode(l.code),
    }));
}
